"use client";
import React, { useRef, useState } from "react";
import styles from "./CustomFileUpload.module.css";

export interface CustomFileUploadProps {
  label: string;
  name: string;
  // keep union so your EnquiryForm.handleChange fits
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;

  // right-side upload button support
  onUpload?: (file: File) => void | Promise<void>;
  uploadText?: string;         // e.g. "Upload Document" / "Uploading..." / "Uploaded"
  uploadDisabled?: boolean;    // <- NEW: disable upload button from parent

  // optional extras
  accept?: string;             // ".pdf,.png,.jpg,.jpeg" (default below)
}

const CustomFileUpload: React.FC<CustomFileUploadProps> = ({
  label,
  name,
  onChange,
  onUpload,
  uploadText = "Upload Document",
  uploadDisabled = false,
  accept = ".pdf,.png,.jpg,.jpeg",
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState<string>("No file chosen");

  const openPicker = () => inputRef.current?.click();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setFileName(f ? f.name : "No file chosen");
    onChange(e); // forward to parent form handler
  };

  const handleUpload = async () => {
    if (!file || !onUpload) return;
    await onUpload(file);
  };

  return (
    <div className={styles.formGroup}>
      <label className={styles.formLabel} htmlFor={`${name}-file`}>
        {label}
      </label>

      {/* Hidden native input */}
      <input
        id={`${name}-file`}
        ref={inputRef}
        type="file"
        name={name}
        accept={accept}
        className={styles.hiddenInput}
        onChange={handleInputChange}
        aria-label={label}
      />

      {/* Dashed row: Choose File | filename | Upload Document */}
      <div className={styles.rowBox}>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={openPicker}
        >
          Choose File
        </button>

        <span className={styles.fileName}>{fileName}</span>

        <button
          type="button"
          className={styles.primaryBtn}
          onClick={handleUpload}
          disabled={uploadDisabled || !file}
        >
          {uploadText}
        </button>
      </div>

      <p className={styles.formNote}>PDF, PNG, JPG up to 10MB</p>
    </div>
  );
};

export default CustomFileUpload;
